import React, { useState, useRef } from 'react';
import useUserSession from 'hooks/useUserSession';
import usePermittedSessionPreferences from 'hooks/usePermittedSessionPreferences';
import useUpdateSessionPreferences from 'hooks/useUpdateSessionPreferences';
import Alert from 'components/Alert';
import CashierNameModal from './CashierNameModal';
import ConnectOrDisconnectModal from './ConnectOrDisconnectModal';
import IconButton from 'components/Button/IconButton';
import Message from 'components/Message';
import SideModal from 'components/SideModal';
import SideModalHeader from 'components/SideModal/Header';
import ToggleSwitch from 'components/ToggleSwitch';
import { Terminal } from 'api/data/payment/types';
import './style.scss';
import { ReaderList } from './ReaderList';

const InPersonPayments = ({ spaceSlug }: { spaceSlug: string }) => {
  const { data: userSession } = useUserSession();
  const { hasInPersonPaymentsPermission } = usePermittedSessionPreferences(spaceSlug);

  if (!userSession || !hasInPersonPaymentsPermission) return null;

  return <InPersonPaymentsModal spaceSlug={spaceSlug} />;
};

const InPersonPaymentsModal = ({ spaceSlug }: { spaceSlug: string }) => {
  const cashierModalRef = useRef<HTMLDivElement>(null);
  const terminalModalRef = useRef<HTMLDivElement>(null);

  const [isOpenInPersonModal, setIsOpenInPersonModal] = useState(false);
  const [isOpenCashierModal, setIsOpenCashierModal] = useState(false);
  const [showMessage, setShowMessage] = useState(false);
  const [selectedTerminal, setSelectedTerminal] = useState<Terminal | null>();
  const [showConnectOrDisconnectModal, setShowConnectOrDisconnectModal] = useState(false);

  const updateSessionPreferences = useUpdateSessionPreferences(spaceSlug);
  const {
    connectedPaymentTerminal,
    paymentTerminals,
    cashierName,
    currentUser,
    acceptInPersonPayments,
    acceptCashAndCheckPayments,
    setSessionTerminal,
    sessionTerminalLoading,
    sessionTerminalError,
    refetchSessionAndTerminals,
  } = usePermittedSessionPreferences(spaceSlug);

  const defaultName = cashierName || currentUser?.fullName || '';

  const toggleAcceptInPersonPayments = async () => {
    const newValue = !acceptInPersonPayments;

    await updateSessionPreferences({
      acceptInPersonPayments: newValue,
      acceptCashAndCheckPayments: newValue,
      ...(newValue && { cashierName: defaultName }),
    });

    if (newValue) {
      setIsOpenCashierModal(true);
    }
  };

  const toggleAcceptCashAndCheckPayments = async () => {
    await updateSessionPreferences({
      acceptCashAndCheckPayments: !acceptCashAndCheckPayments,
    });
  };

  const connectOrDisconnectClicked = (terminal: Terminal) => {
    setSelectedTerminal(terminal);

    if (terminal.activeSession || terminal?.id === connectedPaymentTerminal?.id) {
      return setShowConnectOrDisconnectModal(true);
    }

    if (cashierName) {
      void connectOrDisconnectSelectedTerminal(terminal);
    } else {
      setIsOpenCashierModal(true);
    }
  };

  const connectOrDisconnectSelectedTerminal = async (terminal?: Terminal | null) => {
    const target = terminal ?? selectedTerminal;
    const isCurrentUserTerminal = target?.id === connectedPaymentTerminal?.id;
    const isOtherUserActive = !!target?.activeSession;
    const action = isCurrentUserTerminal || isOtherUserActive ? 'DISCONNECT' : 'CONNECT';

    try {
      if (action === 'CONNECT' && !acceptInPersonPayments && cashierName) {
        await updateSessionPreferences({
          acceptInPersonPayments: true,
          acceptCashAndCheckPayments: true,
        });
      }

      await setSessionTerminal({
        variables: {
          terminalId: target?.id,
          action,
          currentSpace: spaceSlug,
        },
      });

      if (action === 'DISCONNECT' && !isCurrentUserTerminal) {
        await setSessionTerminal({
          variables: {
            terminalId: target?.id,
            action: 'CONNECT',
            currentSpace: spaceSlug,
          },
        });
      }
    } catch (err) {
      if ((err as Error).message !== 'no_active_session') setShowMessage(true);
    } finally {
      if (action === 'CONNECT') setShowMessage(true);
      setSelectedTerminal(undefined);
      setShowConnectOrDisconnectModal(false);
      void refetchSessionAndTerminals();
    }
  };

  const saveCashierNameAndConnectIfNeeded = async (cashierName: string) => {
    await updateSessionPreferences({
      cashierName,
      acceptInPersonPayments: true,
    });

    if (selectedTerminal) void connectOrDisconnectSelectedTerminal();
  };

  const availableTerminals = paymentTerminals.filter(terminal => !terminal.activeSession);
  const terminalsInUse = paymentTerminals.filter(terminal => terminal.activeSession);
  const showReaders = !!paymentTerminals.length && !connectedPaymentTerminal;
  const currentCashier = connectedPaymentTerminal?.activeSession?.cashierName || defaultName;

  return (
    <div className="in-person-payments">
      <IconButton icon="credit_card" className="button-size-m" onClick={() => setIsOpenInPersonModal(true)}>
        In Person Payments
      </IconButton>
      <SideModal
        isOpen={isOpenInPersonModal}
        toggle={setIsOpenInPersonModal}
        allowedShards={[cashierModalRef, terminalModalRef]}
        header={<SideModalHeader title="In Person Payments" onClose={() => setIsOpenInPersonModal(false)} />}>
        <div className="in-person-modal">
          <Message
            type={sessionTerminalError?.message ? 'failed' : 'succeeded'}
            showMessage={(!!connectedPaymentTerminal || !!sessionTerminalError) && showMessage}
            setShowMessage={setShowMessage}>
            {sessionTerminalError?.message
              ? parseErrorMessage(sessionTerminalError.message)
              : 'Device ' + connectedPaymentTerminal?.name + ' connected!'}
          </Message>

          <div className="box accept-in-person-box">
            <ToggleSwitch
              label="Accept in person payments"
              toggleValue={acceptInPersonPayments}
              handleOnChange={() => void toggleAcceptInPersonPayments()}
            />

            {acceptInPersonPayments && (
              <div className="in-person-wrapper">
                <div className="horizontal-line"></div>
                <div className="row space-between cashier-wrapper">
                  <span className="size-xxs subtitle-darker label">Cashier</span>
                  <IconButton
                    aria-label="Edit cashier name"
                    icon="edit"
                    className="button-size-m "
                    onClick={() => setIsOpenCashierModal(true)}
                    iconSide="RIGHT">
                    {currentCashier}
                  </IconButton>
                </div>
                <div className="box">
                  {!!connectedPaymentTerminal && (
                    <div className="row space-between align-center connected-terminal">
                      <span className="subtitle-x-small">{connectedPaymentTerminal.name}</span>
                      <button
                        className="button-size-ml font-size-xxxs button-outline"
                        onClick={() => connectOrDisconnectClicked(connectedPaymentTerminal)}>
                        Disconnect
                      </button>
                    </div>
                  )}
                  <ToggleSwitch
                    label="Cash and check"
                    toggleValue={acceptCashAndCheckPayments}
                    handleOnChange={() => void toggleAcceptCashAndCheckPayments()}
                  />
                </div>

                <Alert
                  type="informative"
                  icon="warning_circle_solid"
                  content={
                    showReaders ? 'Select a reader below to accept card payments' : 'Disconnect to see other readers'
                  }
                />
              </div>
            )}
          </div>

          {showReaders && !!availableTerminals.length && (
            <>
              <p className="subtitle-x-small neutral">Available Readers ({availableTerminals.length})</p>
              <ReaderList
                terminals={availableTerminals}
                sessionTerminalLoading={sessionTerminalLoading}
                onClickTerminal={terminal => connectOrDisconnectClicked(terminal)}
              />
            </>
          )}

          {showReaders && !!terminalsInUse.length && (
            <>
              <p className="subtitle-x-small neutral">Readers in use by other people ({terminalsInUse.length})</p>
              <ReaderList
                terminals={terminalsInUse}
                sessionTerminalLoading={sessionTerminalLoading}
                onClickTerminal={terminal => connectOrDisconnectClicked(terminal)}
              />
            </>
          )}
        </div>
      </SideModal>

      <CashierNameModal
        modalRef={cashierModalRef}
        setIsOpen={setIsOpenCashierModal}
        isOpen={isOpenCashierModal}
        handleSaveCashierName={cashierName => saveCashierNameAndConnectIfNeeded(cashierName)}
        defaultName={defaultName}
      />

      <ConnectOrDisconnectModal
        showConfirmation={showConnectOrDisconnectModal}
        terminalModalRef={terminalModalRef}
        onCancel={() => setShowConnectOrDisconnectModal(false)}
        onConfirm={() => void connectOrDisconnectSelectedTerminal()}
        selectedTerminal={selectedTerminal}
        connectedTerminal={connectedPaymentTerminal}
      />
    </div>
  );
};

export default InPersonPayments;

const parseErrorMessage = (message: string) => {
  if (message === 'payment_terminal_in_use') return 'The Reader you selected is in use. Please try again';
  else return message;
};
